/**
 * The app-wide Pomodoro focus timer.
 *
 * Mounted in `app/provider.tsx`, above the routes, so a block keeps counting
 * as the student moves between pages. Even so, nothing here depends on
 * staying mounted: the state is in localStorage and every reading of it is
 * derived from timestamps (see `lib/focus/focusMachine`), so a refresh reads
 * the same timer - and the storage event keeps two tabs on one timer.
 *
 * Keyed per user, so a shared computer never shows someone else's block.
 *
 * Finished blocks are also mirrored to the backend (`useFocusLogSync`) when
 * someone is signed in and the app is on real data, so the log follows the
 * student across devices. The running timer itself never leaves the browser.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocalStorage } from '@ai-tutor/ui';
import { track } from '@/config/observability';
import { AuthContext } from '@/contexts/AuthContext';
import { useDataMode } from '@/contexts/DataModeContext';
import {
  INITIAL_FOCUS_STATE,
  normalizeFocusState,
  reduceFocus,
  type FocusAction,
  type FocusState,
} from '@/lib/focus/focusMachine';
import {
  isRecorded,
  recordBlock,
  summarizeDay,
  type DaySummary,
  type FocusLogEntry,
} from '@/lib/focus/focusLog';
import {
  DEFAULT_FOCUS_SETTINGS,
  type FocusSettings,
} from '@/lib/focus/focusAlerts';
import {
  useFocusTicker,
  useFocusTitle,
  useFocusTransitions,
} from '@/hooks/focus/useFocusEffects';
import {
  useFocusLogSync,
  type FocusSyncStatus,
} from '@/hooks/focus/useFocusLogSync';

export interface FocusTimerContextValue {
  state: FocusState;
  dispatch: (action: FocusAction) => void;
  settings: FocusSettings;
  setSettings: (settings: FocusSettings) => void;
  today: DaySummary;
  /** Every recorded block on this device, oldest first (90 days). */
  log: FocusLogEntry[];
  /** How the log stands against the server's copy. */
  sync: FocusSyncStatus;
  breakSheetOpen: boolean;
  openBreakSheet: () => void;
  closeBreakSheet: () => void;
}

const FocusTimerContext = createContext<FocusTimerContextValue | null>(null);

/** Storage keys, exported so stories and tests can seed and clear them. */
export const focusStorageKeys = (userId: string) => ({
  state: `xonera:focus:${userId}`,
  log: `xonera:focus-log:${userId}`,
  settings: `xonera:focus-settings:${userId}`,
});

/** The id used before auth resolves, and in stories with no session. */
export const GUEST_USER_ID = 'guest';

function useFocusLog(
  userId: string,
  state: FocusState
): { today: DaySummary; log: FocusLogEntry[]; sync: FocusSyncStatus } {
  const keys = focusStorageKeys(userId);
  const [log, setLog] = useLocalStorage<FocusLogEntry[]>(keys.log, []);
  const block = state.lastBlock;
  const { isApi } = useDataMode();
  const sync = useFocusLogSync({
    enabled: isApi && userId !== GUEST_USER_ID,
    userId,
    log,
    setLog,
  });

  useEffect(() => {
    if (block && !isRecorded(log, block)) {
      setLog(recordBlock(log, block, Date.now()));
    }
  }, [block, log, setLog]);

  // The day only rolls over on a re-render, which a finished block causes.
  const today = useMemo(() => summarizeDay(log, Date.now()), [log]);
  return { today, log, sync };
}

/** Analytics for the two focus actions that matter: a start and a session end. */
function trackFocusAction(
  action: FocusAction,
  prev: FocusState,
  next: FocusState
): void {
  if (action.type === 'start') {
    track('focus_session_started', {
      mode: action.mode,
      focus_minutes: action.focusMinutes,
    });
  } else if (action.type === 'endSession') {
    track('focus_session_finished', {
      mode: prev.mode,
      focus_minutes: prev.focusMinutes,
      rounds_completed: next.completedRounds,
    });
  }
}

function FocusTimerStore({
  userId,
  children,
  sheet,
}: {
  userId: string;
  children: ReactNode;
  sheet?: ReactNode;
}) {
  const keys = focusStorageKeys(userId);
  const [stored, setStored] = useLocalStorage<FocusState>(
    keys.state,
    INITIAL_FOCUS_STATE
  );
  const [storedSettings, setSettings] = useLocalStorage<FocusSettings>(
    keys.settings,
    DEFAULT_FOCUS_SETTINGS
  );
  // Normalise so state written by an older build still has every field (the
  // `usePersistentSession` rule) and v1's fixed modes map onto v2's.
  const state = useMemo(() => normalizeFocusState(stored), [stored]);
  const settings = useMemo(
    () => ({ ...DEFAULT_FOCUS_SETTINGS, ...storedSettings }),
    [storedSettings]
  );

  // Reduce against the latest value, not a render-time closure, so two
  // dispatches in one handler (save recap, then next block) both land.
  const latest = useRef(state);
  latest.current = state;
  const dispatch = useCallback(
    (action: FocusAction) => {
      const prev = latest.current;
      const next = reduceFocus(prev, action, Date.now());
      if (next === prev) return;
      latest.current = next;
      trackFocusAction(action, prev, next);
      setStored(next);
    },
    [setStored]
  );

  const [breakSheetOpen, setBreakSheetOpen] = useState(false);
  const openBreakSheet = useCallback(() => setBreakSheetOpen(true), []);
  const closeBreakSheet = useCallback(() => setBreakSheetOpen(false), []);

  const { today, log, sync } = useFocusLog(userId, state);
  useFocusTicker(state.phase, dispatch);
  useFocusTitle(state, settings.tabTitle);
  useFocusTransitions(state, settings, openBreakSheet);

  const value = useMemo<FocusTimerContextValue>(
    () => ({
      state,
      dispatch,
      settings,
      setSettings,
      today,
      log,
      sync,
      breakSheetOpen,
      openBreakSheet,
      closeBreakSheet,
    }),
    [
      state,
      dispatch,
      settings,
      setSettings,
      today,
      log,
      sync,
      breakSheetOpen,
      openBreakSheet,
      closeBreakSheet,
    ]
  );

  return (
    <FocusTimerContext.Provider value={value}>
      {children}
      {sheet}
    </FocusTimerContext.Provider>
  );
}

/**
 * `sheet` is the break sheet, passed in rather than imported so this context
 * does not depend on the feature's view layer.
 */
export function FocusTimerProvider({
  children,
  sheet,
}: {
  children: ReactNode;
  sheet?: ReactNode;
}) {
  // Read optionally: the story decorator mounts this outside AuthProvider.
  const auth = useContext(AuthContext);
  const userId = auth?.user?.id ?? GUEST_USER_ID;
  // Keyed so a different user re-reads storage under their own key rather
  // than inheriting the previous user's in-memory timer.
  return (
    <FocusTimerStore key={userId} userId={userId} sheet={sheet}>
      {children}
    </FocusTimerStore>
  );
}

/** The timer, or `null` outside `AppLayout` (e.g. a bare Navbar story). */
export function useOptionalFocusTimer(): FocusTimerContextValue | null {
  return useContext(FocusTimerContext);
}

export function useFocusTimer(): FocusTimerContextValue {
  const context = useContext(FocusTimerContext);
  if (!context) {
    throw new Error('useFocusTimer must be used inside FocusTimerProvider');
  }
  return context;
}
